/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getSSOConfiguration} from '@ion/core/src/services/Connect';
import {SSOConfiguration} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime, onClientErrorHandler} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type useSSOConfigurationtionResult = UseDefaultQueryResult & {
  ssoConfig: SSOConfiguration;
  isConfigLoading: boolean;
};

/**
 * Retrieves the ssoConfiguration information of the account with the given configuration id.
 *
 * ```typescript
 *
 * import useSSOConfiguration from '@ion/query/src/hooks/';
 *
 * const {ssoConfig} = useSSOConfiguration();
 *
 * ```
 *
 * @param configurationId of the ssoConfiguration to retrieve the ssoConfiguration list.
 * @returns a query result
 */
export const useSSOConfiguration = (
  configurationId,
  enabled = true
): useSSOConfigurationtionResult => {
  const key = useMemo(() => (
    createKey(QueryKey.GET_SSO_CONFIGURATION, configurationId)
  ), [configurationId]);

  const query = useQuery(key, async() => {
    try {
      const results = await getSSOConfiguration(configurationId);
      return results;
    } catch (err) {
      return onClientErrorHandler(err);
    }
  }, {enabled});

  return createQueryResult(query, {
    ssoConfig       : query.data || {},
    isConfigLoading : query.isFetching
  }) as useSSOConfigurationtionResult;
};

export default useSSOConfiguration;
